import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Package, Truck, CheckCircle, Clock, MapPin, 
  ArrowLeft, Loader2, FileText, AlertCircle 
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrackOrder = () => {
  const { orderId: urlOrderId } = useParams();
  const [orderId, setOrderId] = useState(urlOrderId || '');
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (urlOrderId) {
      fetchTracking(urlOrderId);
    }
  }, [urlOrderId]);

  const fetchTracking = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/orders/track/${id}`);
      setTracking(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Order not found. Please check your order ID.');
      setTracking(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (orderId.trim()) {
      fetchTracking(orderId.trim());
    }
  };

  const getStatusIcon = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('delivered')) return <CheckCircle className="w-6 h-6 text-green-500" />;
    if (statusLower.includes('shipped') || statusLower.includes('transit')) return <Truck className="w-6 h-6 text-blue-500" />;
    if (statusLower.includes('out for delivery')) return <MapPin className="w-6 h-6 text-orange-500" />;
    if (statusLower.includes('cancelled') || statusLower.includes('return')) return <AlertCircle className="w-6 h-6 text-red-500" />;
    return <Package className="w-6 h-6 text-primary" />;
  };

  const getStatusStep = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('delivered')) return 4;
    if (statusLower.includes('out for delivery')) return 3;
    if (statusLower.includes('shipped') || statusLower.includes('transit')) return 2;
    if (statusLower.includes('confirmed') || statusLower.includes('processing')) return 1;
    return 0;
  };

  const statusSteps = [
    { label: 'Order Placed', icon: Package },
    { label: 'Confirmed', icon: CheckCircle },
    { label: 'Shipped', icon: Truck },
    { label: 'Out for Delivery', icon: MapPin },
    { label: 'Delivered', icon: CheckCircle },
  ];

  const currentStep = tracking ? getStatusStep(tracking.order_status) : 0;

  return (
    <div className="section-spacing" data-testid="track-order-page">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link to="/dashboard" className="inline-flex items-center text-muted-foreground hover:text-primary mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Link>
            <h1 className="text-4xl font-heading font-bold">Track Your Order</h1>
          </div>

          {/* Search Form */}
          {!urlOrderId && (
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-card mb-8">
              <label className="block text-lg font-medium mb-4">Enter Order ID</label>
              <div className="flex gap-4">
                <Input
                  data-testid="order-id-input"
                  placeholder="e.g., abc12345-6789-..."
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  className="flex-1"
                />
                <Button type="submit" disabled={loading || !orderId.trim()} data-testid="track-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Track'}
                </Button>
              </div>
            </form>
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
              <p className="text-muted-foreground">Loading tracking information...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
              <p className="text-red-600 font-medium">{error}</p>
            </div>
          )}

          {/* Tracking Information */}
          {tracking && !loading && (
            <div className="space-y-8">
              {/* Order Summary */}
              <div className="bg-white rounded-2xl p-8 shadow-card">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-heading font-bold">Order Status</h2>
                  <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                    tracking.payment_status === 'paid' 
                      ? 'bg-green-100 text-green-700'
                      : tracking.payment_status === 'cod_pending'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {tracking.payment_status === 'paid' ? 'Paid' : 
                     tracking.payment_status === 'cod_pending' ? 'COD - Pay on Delivery' : 
                     tracking.payment_status}
                  </span>
                </div>
                
                <div className="text-sm text-muted-foreground mb-6">
                  Order ID: <span className="font-mono">{tracking.order_id}</span>
                  {tracking.tracking_number && (
                    <span className="ml-4">
                      Tracking #: <span className="font-mono">{tracking.tracking_number}</span>
                    </span>
                  )}
                </div>

                {/* Status Progress Bar */}
                <div className="relative">
                  {/* Progress Line */}
                  <div className="absolute top-6 left-0 right-0 h-1 bg-gray-200 rounded-full">
                    <div 
                      className="h-full bg-primary rounded-full transition-all duration-500"
                      style={{ width: `${(currentStep / (statusSteps.length - 1)) * 100}%` }}
                    />
                  </div>
                  
                  {/* Status Steps */}
                  <div className="relative flex justify-between">
                    {statusSteps.map((step, index) => {
                      const isCompleted = index <= currentStep;
                      const isCurrent = index === currentStep;
                      const Icon = step.icon;
                      
                      return (
                        <div key={step.label} className="flex flex-col items-center">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center z-10 transition-all ${
                            isCompleted 
                              ? 'bg-primary text-white' 
                              : 'bg-gray-200 text-gray-400'
                          } ${isCurrent ? 'ring-4 ring-primary/30' : ''}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <p className={`text-xs mt-2 text-center max-w-[80px] ${
                            isCompleted ? 'text-primary font-medium' : 'text-muted-foreground'
                          }`}>
                            {step.label}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Tracking History */}
              {tracking.tracking_history && tracking.tracking_history.length > 0 && (
                <div className="bg-white rounded-2xl p-8 shadow-card">
                  <h2 className="text-xl font-heading font-bold mb-6">Tracking History</h2>
                  <div className="space-y-6">
                    {[...tracking.tracking_history].reverse().map((entry, index) => (
                      <div key={index} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            index === 0 ? 'bg-primary text-white' : 'bg-muted'
                          }`}>
                            {getStatusIcon(entry.status)}
                          </div>
                          {index < tracking.tracking_history.length - 1 && (
                            <div className="w-0.5 h-full min-h-[40px] bg-border mt-2" />
                          )}
                        </div>
                        <div className="flex-1 pb-6">
                          <p className="font-semibold">{entry.status}</p>
                          <p className="text-sm text-muted-foreground">{entry.message}</p>
                          {entry.location && (
                            <p className="text-sm text-muted-foreground flex items-center mt-1">
                              <MapPin className="w-4 h-4 mr-1" />
                              {entry.location}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(entry.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button variant="outline">Need Help?</Button>
                </Link>
                <Link to="/products">
                  <Button>Continue Shopping</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrackOrder;
